<template>
    <v-dialog v-model="dialogSwitch" max-width="900" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center justify-space-between">
                <span>OTA Download Timeline - {{ deviceSlno }}</span>
                <v-btn icon="mdi-close" variant="text" size="small" @click="dialogSwitch = false"/>
            </v-card-title>
            <v-card-text>
                <div v-if="loading" class="text-center py-8">
                    <v-progress-circular indeterminate color="primary"/>
                </div>
                <template v-else-if="points.length">
                    <!-- Chart -->
                    <div ref="chartContainer" style="height: 300px; position: relative; border: 1px solid #e0e0e0; border-radius: 8px; padding: 16px; background: #fafafa">
                        <svg :viewBox="`0 0 ${chartWidth} ${chartHeight}`" width="100%" height="100%"
                             style="overflow: visible">
                            <!-- Grid lines -->
                            <line v-for="pct in [0, 25, 50, 75, 100]" :key="'g'+pct"
                                  :x1="chartPadLeft" :y1="yScale(pct)"
                                  :x2="chartWidth - chartPadRight" :y2="yScale(pct)"
                                  stroke="#e0e0e0" stroke-width="0.5"/>
                            <!-- Y-axis labels -->
                            <text v-for="pct in [0, 25, 50, 75, 100]" :key="'yl'+pct"
                                  :x="chartPadLeft - 4" :y="yScale(pct) + 4"
                                  text-anchor="end" font-size="10" fill="#999">{{ pct }}%</text>

                            <!-- Progress line -->
                            <polyline :points="chartPolyline" fill="none" stroke="#1976D2" stroke-width="2"
                                      stroke-linejoin="round"/>

                            <!-- Gap markers (device asleep) -->
                            <template v-for="(gap, i) in gaps" :key="'gap'+i">
                                <rect :x="xScale(gap.startIdx)" :y="4"
                                      :width="Math.max(xScale(gap.endIdx) - xScale(gap.startIdx), 2)"
                                      :height="chartHeight - 8"
                                      fill="#FFF3E0" opacity="0.6"/>
                                <text :x="(xScale(gap.startIdx) + xScale(gap.endIdx)) / 2"
                                      :y="chartHeight / 2" text-anchor="middle" font-size="8" fill="#E65100">
                                    {{ gap.label }}
                                </text>
                            </template>

                            <!-- Event markers -->
                            <circle v-for="(evt, i) in eventMarkers" :key="'e'+i"
                                    :cx="xScale(evt.idx)" :cy="yScale(evt.progress)"
                                    :r="4" :fill="evt.color" stroke="white" stroke-width="1">
                                <title>{{ evt.label }}</title>
                            </circle>
                        </svg>
                    </div>

                    <!-- X-axis time labels -->
                    <div class="d-flex justify-space-between px-4 mt-1">
                        <small class="text-grey">{{ formatTime(points[0]?.time) }}</small>
                        <small class="text-grey" v-if="points.length > 1">{{ formatTime(points[points.length - 1]?.time) }}</small>
                    </div>

                    <!-- Stats cards -->
                    <v-row class="mt-4" dense>
                        <v-col cols="6" sm="3">
                            <v-card variant="tonal" color="blue" class="pa-3 text-center">
                                <div class="text-h6">{{ stats.download_sessions }}</div>
                                <div class="text-caption">Sessions</div>
                            </v-card>
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-card variant="tonal" color="green" class="pa-3 text-center">
                                <div class="text-h6">{{ stats.total_attempts }}</div>
                                <div class="text-caption">HTTP Chunks</div>
                            </v-card>
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-card variant="tonal" color="orange" class="pa-3 text-center">
                                <div class="text-h6">{{ totalDuration }}</div>
                                <div class="text-caption">Total Duration</div>
                            </v-card>
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-card variant="tonal" color="purple" class="pa-3 text-center">
                                <div class="text-h6">{{ points.length }}</div>
                                <div class="text-caption">Log Entries</div>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Event legend -->
                    <div class="d-flex ga-4 mt-3 justify-center">
                        <div class="d-flex align-center"><span class="dot bg-green mr-1"/><small>Queued</small></div>
                        <div class="d-flex align-center"><span class="dot bg-blue mr-1"/><small>Downloading</small></div>
                        <div class="d-flex align-center"><span class="dot bg-amber mr-1"/><small>Device Asleep</small></div>
                        <div class="d-flex align-center"><span class="dot bg-yellow mr-1" style="border: 2px solid gold"/><small>Upgraded</small></div>
                    </div>
                </template>
                <div v-else class="text-center py-8 text-grey">
                    No download logs found for this device.
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, computed, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {formatDateTime} from '@/composables/useTimezone';

const props = defineProps({
    modelValue: Boolean,
    campaignId: Number,
    deviceId: Number,
    deviceSlno: String,
});
const emits = defineEmits(['update:modelValue']);
const dialogSwitch = computed({
    get: () => props.modelValue,
    set: (v) => emits('update:modelValue', v),
});

const loading = ref(false);
const points = ref([]);
const stats = ref({});

// Chart dimensions
const chartWidth = 800;
const chartHeight = 250;
const chartPadLeft = 40;
const chartPadRight = 10;

const xScale = (idx) => chartPadLeft + (idx / Math.max(points.value.length - 1, 1)) * (chartWidth - chartPadLeft - chartPadRight);
const yScale = (pct) => chartHeight - (pct / 100) * (chartHeight - 20) - 10;

const chartPolyline = computed(() => {
    const pts = points.value;
    const result = [];
    // Downsample for performance: max ~1000 points in the SVG polyline
    const step = Math.max(1, Math.floor(pts.length / 1000));
    for (let i = 0; i < pts.length; i += step) {
        const p = pts[i];
        if (p.progress !== null) {
            result.push(`${xScale(i)},${yScale(p.progress)}`);
        }
    }
    // Always include the last point
    if (pts.length > 0 && pts[pts.length - 1].progress !== null) {
        result.push(`${xScale(pts.length - 1)},${yScale(pts[pts.length - 1].progress)}`);
    }
    return result.join(' ');
});

// Detect gaps (device asleep > 5 minutes)
const gaps = computed(() => {
    const result = [];
    for (let i = 1; i < points.value.length; i++) {
        const prev = new Date(points.value[i - 1].time).getTime();
        const curr = new Date(points.value[i].time).getTime();
        const diffMin = (curr - prev) / 60000;
        if (diffMin > 5) {
            const hours = Math.floor(diffMin / 60);
            const mins = Math.round(diffMin % 60);
            result.push({
                startIdx: i - 1,
                endIdx: i,
                label: hours > 0 ? `${hours}h ${mins}m` : `${mins}m`,
            });
        }
    }
    return result;
});

// Event markers (queued, upgraded, progress drops)
const eventMarkers = computed(() => {
    const markers = [];
    points.value.forEach((p, i) => {
        if (p.event?.includes('QUEUED') && !p.event?.includes('ACKED')) {
            markers.push({idx: i, progress: 0, color: '#4CAF50', label: `Queued: ${formatTime(p.time)}`});
        } else if (p.event?.includes('QUEUE_ACKED')) {
            markers.push({idx: i, progress: 0, color: '#2196F3', label: `Acked: ${formatTime(p.time)}`});
        } else if (p.event?.includes('UPGRADED')) {
            markers.push({idx: i, progress: 100, color: '#FFD700', label: `Upgraded: ${formatTime(p.time)}`});
        }
    });
    return markers;
});

const totalDuration = computed(() => {
    if (!stats.value.first_event || !stats.value.last_event) return '-';
    const ms = new Date(stats.value.last_event).getTime() - new Date(stats.value.first_event).getTime();
    const hours = Math.floor(ms / 3600000);
    const mins = Math.round((ms % 3600000) / 60000);
    if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h`;
    }
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
});

const formatTime = (iso) => {
    if (!iso) return '';
    return formatDateTime(iso);
};

const fetchTimeline = () => {
    if (!props.campaignId || !props.deviceId) return;
    loading.value = true;
    send('get', `admin/ota/campaigns/${props.campaignId}/device-timeline/${props.deviceId}`)
        .then((response) => {
            const data = response.data.data;
            points.value = data.points || [];
            stats.value = data.stats || {};
        })
        .catch(() => {
            points.value = [];
            stats.value = {};
        })
        .finally(() => {
            loading.value = false;
        });
};

watch(() => props.modelValue, (val) => {
    if (val) fetchTimeline();
});
</script>

<style scoped>
.dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}
</style>
